import { FC, useState, useEffect } from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import { CourseEvent } from '../../../models/CourseEvent';
import { Course } from '../../../models/Course';
import Form from 'react-bootstrap/Form';
import DateTimePicker from 'react-datetime-picker';
import 'react-datetime-picker/dist/DateTimePicker.css';
import 'react-calendar/dist/Calendar.css';
import 'react-clock/dist/Clock.css';
import { Calendar } from 'react-bootstrap-icons';
import { X } from 'react-bootstrap-icons';
import Container from 'react-bootstrap/esm/Container';
import Row from 'react-bootstrap/esm/Row';
import Col from 'react-bootstrap/esm/Col';
import CourseEventEndpoint from '../../../../api/endpoint/CourseEventEndpoint';
import CourseEndpoint from '../../../../api/endpoint/CourseEndpoint';
import { displayDateTimeForDatepicker, inputAsVtTime } from '../../../utils/DateManagement';

interface CourseEventProps {
    courseEvent: CourseEvent;
    show: boolean;
    onHide: () => void;
    isNew: boolean;
    initiateReload: () => void;
}

type ValuePiece = Date | null;

type Value = ValuePiece | [ValuePiece, ValuePiece];

export const CourseEventModal: FC<CourseEventProps> = ({courseEvent, show, onHide, isNew, initiateReload}) => {
    const [courseID, setCourseID] = useState<number>(courseEvent.course_id);
    const [hours, setHours] = useState(courseEvent.hours);
    const [maxRegistrations, setMaxRegistrations] = useState(courseEvent.max_registrations);
    const [startDate, setStartDate] = useState<Value>(displayDateTimeForDatepicker(courseEvent.start_date));
    const [endDate, setEndDate] = useState<Value>(displayDateTimeForDatepicker(courseEvent.end_date));
    const [openDate, setOpenDate] = useState<Value>(displayDateTimeForDatepicker(courseEvent.open_date));
    const [closeDate, setCloseDate] = useState<Value>(displayDateTimeForDatepicker(courseEvent.close_date));
    const [location, setLocation] = useState(courseEvent.location? courseEvent.location : 'In-Person');
    const [locationDetails, setLocationDetails] = useState(courseEvent.location_details);

    const [courses, setCourses] = useState<Course[] | undefined>(undefined);
    
    const [validated, setValidated] = useState(false);
    const [isInitialLoad, setIsInitialLoad] = useState(true);

    useEffect(() => {
        if(isInitialLoad){
            getCourses();
            setIsInitialLoad(false);
        }
    }, [isInitialLoad]);

    useEffect(() => {
        if(courseEvent.course_id === 0 && (courses && courses.length > 0)) {
            setCourseID(courses[0].course_id);
        }
    }, [courses]);

    const getCourses = async () => {
        try{
            const result = await CourseEndpoint.GetAll();
            const coursesResult: Course[] = result.data.map((row: any) => ({
                course_id: Number(row.course_id),
                title: row.title,
                description: row.description,
            }));
            setCourses(coursesResult);

        } catch(err: any) {
            console.error('Search error: ', err);
        }
    };

    const handleSubmit = async (event: any) => {
        const form = event.currentTarget;
        if (form.checkValidity() === false) {
            event.preventDefault();
            event.stopPropagation();
        }
        else if(form.checkValidity() === true) {
            event.preventDefault();
            if(isNew){
                await handleCreateNew();
            } else {
                await handleUpdate();
            }
            initiateReload();
            onHide();
        }

        setValidated(true);
    };

    const handleCreateNew = async () => {
        const linkedCourse = courses?.filter((course) => course.course_id === courseID)[0];

        const newCourseEvent = {
            title: linkedCourse!.title,
            course_id: courseID,
            hours: hours,
            location: location,
            location_details: locationDetails,
            close_date: inputAsVtTime(closeDate as Date),
            start_date: inputAsVtTime(startDate as Date),
            end_date: inputAsVtTime(endDate as Date),
            open_date: inputAsVtTime(openDate as Date),
            max_registrations: maxRegistrations,
            current_registrations: 0
        }

        const createResult = await CourseEventEndpoint.Create(newCourseEvent);

        if(!createResult){
            console.error('Error creating course');
        }
    };

    const handleUpdate = async () => {
        const updateFields: any = {course_event_id: courseEvent.course_event_id};

        if(courseID !== courseEvent.course_id) updateFields.course_id = courseID;
        if(hours !== courseEvent.hours) updateFields.hours = hours;
        if(maxRegistrations !== courseEvent.max_registrations) updateFields.max_registrations = maxRegistrations;
        if(location !== courseEvent.location) updateFields.location = location;
        if(locationDetails !== courseEvent.location_details) updateFields.location_details = locationDetails;
        if(inputAsVtTime(startDate as Date).getTime() !== courseEvent.start_date.getTime()) updateFields.start_date = inputAsVtTime(startDate as Date); 
        if(inputAsVtTime(endDate as Date).getTime() !== courseEvent.end_date.getTime()) updateFields.end_date = inputAsVtTime(endDate as Date); 
        if(inputAsVtTime(openDate as Date).getTime() !== courseEvent.open_date.getTime()) updateFields.open_date = inputAsVtTime(openDate as Date); 
        if(inputAsVtTime(closeDate as Date).getTime() !== courseEvent.close_date.getTime()) updateFields.close_date = inputAsVtTime(closeDate as Date); 

        const updateResult = await CourseEventEndpoint.Update(updateFields);

        if(!updateResult){
            console.error('Error updating course');
        }
    };

    return (
        <Modal
            show={show}
            size="xl"
            aria-labelledby="course-title"
            centered
            onHide={onHide}
        >
            <Modal.Header closeButton>
                <Modal.Title id="course-title">
                    {!isNew &&
                        <>Edit Course Event {courseEvent.course_event_id}</>
                    }
                    {isNew && 
                        <>Add Course Event</>
                    }
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form noValidate validated={validated} onSubmit={handleSubmit}>
                    <Container>
                        <Row style={{marginBottom:'1em'}}>
                            <Col lg={7} md={12}>
                                <Form.Group className="mb-3" controlId="course-select">
                                    <Form.Label>Course</Form.Label>
                                    <Form.Select 
                                        required
                                        value={courseID}
                                        onChange={(e:any) => setCourseID(Number(e.target.value))}
                                    >
                                        {courses && courses.length > 0 &&
                                            courses.map((course:Course) => {
                                                return(
                                                    <option key={course.course_id} value={course.course_id}>{course.title}</option>
                                                )
                                            })
                                        }
                                    </Form.Select>
                                    <Form.Control.Feedback type="invalid">Course title is required.</Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col lg={2} md={4}>
                                <Form.Group className="mb-3" controlId="hours-input">
                                    <Form.Label>Hours</Form.Label>
                                    <Form.Control 
                                        required
                                        value={hours}
                                        type="number"
                                        min={1}
                                        max={100}
                                        onChange={(e:any) => setHours(Number(e.target.value))}
                                    />
                                    <Form.Control.Feedback type="invalid">Must be a number from 1 to 100.</Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col lg={3} md={8}>
                                <Form.Group className="mb-3" controlId="max-reg-input">
                                    <Form.Label>Max Registrations</Form.Label>
                                    <Form.Control 
                                        required
                                        value={maxRegistrations}
                                        type="number"
                                        min={1}
                                        max={100}
                                        onChange={(e:any) => setMaxRegistrations(Number(e.target.value))}
                                    />
                                    <Form.Control.Feedback type="invalid">Must be a number from 1 to 100.</Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>

                        
                        <Row style={{marginBottom:'1em'}}>
                            <Col lg={6} md={12}>
                                <Form.Group className="mb-3" controlId="start-date-input">
                                    <label style={{width:'100%'}}>
                                        Start Date
                                    <DateTimePicker 
                                        monthAriaLabel='start-date-month'
                                        dayAriaLabel='start-date-day'
                                        yearAriaLabel='start-date-year'
                                        hourAriaLabel='start-date-hour'
                                        minuteAriaLabel='start-date-minute'
                                        amPmAriaLabel='start-date-am-pm'
                                        clearAriaLabel='clear-start-date'
                                        nativeInputAriaLabel='start-date-input'
                                        calendarAriaLabel='start-date-calendar-button'
                                        value={startDate}
                                        onChange={setStartDate} 
                                        className="form-control"
                                        calendarIcon={<Calendar color="#707070" />}
                                        clearIcon={<X color="#707070" size={25} />}
                                        disableClock={true}
                                    />
                                    </label>
                                </Form.Group>
                            </Col>

                            <Col lg={6} md={12}>
                                <Form.Group className="mb-3" controlId="end-date-input">
                                    <label style={{width:'100%'}}>
                                        End Date
                                        <DateTimePicker 
                                            monthAriaLabel='end-date-month'
                                            dayAriaLabel='end-date-day'
                                            yearAriaLabel='end-date-year'
                                            hourAriaLabel='end-date-hour'
                                            minuteAriaLabel='end-date-minute'
                                            amPmAriaLabel='end-date-am-pm'
                                            clearAriaLabel='clear-end-date'
                                            nativeInputAriaLabel='end-date-input'
                                            calendarAriaLabel='end-date-calendar-button'
                                            value={endDate}
                                            onChange={setEndDate} 
                                            className="form-control"
                                            calendarIcon={<Calendar color="#707070" />}
                                            clearIcon={<X color="#707070" size={25} />}
                                            disableClock={true}
                                        />
                                    </label>
                                </Form.Group>
                            </Col>
                        </Row>
                        

                        
                        <Row style={{marginBottom:'1em'}}>
                            <Col lg={6} md={12}>
                                <Form.Group className="mb-3" controlId="open-date-input">
                                    <label style={{width:'100%'}}>
                                        Registration Open Date
                                        <DateTimePicker 
                                            monthAriaLabel='registration-opens-date-month'
                                            dayAriaLabel='registration-opens-date-day'
                                            yearAriaLabel='registration-opens-date-year'
                                            hourAriaLabel='registration-opens-date-hour'
                                            minuteAriaLabel='registration-opens-date-minute'
                                            amPmAriaLabel='registration-opens-date-am-pm'
                                            clearAriaLabel='clear-registration-opens-date'
                                            nativeInputAriaLabel='registration-opens-date-input'
                                            calendarAriaLabel='registration-opens-calendar-button'
                                            id="registration-open-picker"
                                            value={openDate}
                                            onChange={setOpenDate} 
                                            className="form-control"
                                            calendarIcon={<Calendar color="#707070" />}
                                            clearIcon={<X color="#707070" size={25} />}
                                            aria-labelled-by="open-date-lbl"
                                            disableClock={true}
                                        />
                                    </label>
                                </Form.Group>
                            </Col>

                            <Col lg={6} md={12}>
                                <Form.Group className="mb-3" controlId="close-date-input">
                                    <label style={{width:'100%'}}>
                                        Registration Close Date
                                        <DateTimePicker 
                                            monthAriaLabel='registration-closes-date-month'
                                            dayAriaLabel='registration-closes-date-day'
                                            yearAriaLabel='registration-closes-date-year'
                                            hourAriaLabel='registration-closes-date-hour'
                                            minuteAriaLabel='registration-closes-date-minute'
                                            amPmAriaLabel='registration-closes-date-am-pm'
                                            clearAriaLabel='clear-registration-closes-date'
                                            nativeInputAriaLabel='registration-closes-date-input'
                                            calendarAriaLabel='registration-closes-calendar-button'
                                            id="registration-close-date-picker"
                                            value={closeDate}
                                            onChange={setCloseDate} 
                                            className="form-control"
                                            calendarIcon={<Calendar color="#707070" />}
                                            clearIcon={<X color="#707070" size={25} />}
                                            disableClock={true}
                                        />
                                    </label>
                                </Form.Group>
                            </Col>
                        </Row>

                        
                        <Row>
                            <Col lg={3} md={12}>
                                <Form.Group className="mb-3" controlId="location-select">
                                    <Form.Label>Location</Form.Label>
                                    <Form.Select 
                                        required
                                        value={location}
                                        onChange={(e:any) => setLocation(e.target.value)}
                                    >
                                        <option value="In-Person">In-Person</option>
                                        <option value="Webinar">Webinar</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>

                            <Col lg={9} md={12}>
                                <Form.Group className="mb-3" controlId="location-details-input">
                                    <Form.Label>Location Details</Form.Label>
                                    <Form.Control  
                                        as="textarea" 
                                        rows={1} 
                                        value={locationDetails} 
                                        onChange={(e:any) => setLocationDetails(e.target.value)}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        

                        <Modal.Footer>
                            <Button variant="secondary" onClick={onHide}>Close</Button>
                            <Button variant="primary" className="text-light" type="submit">Submit</Button>
                        </Modal.Footer>
                    </Container>
                </Form>
            </Modal.Body>
        </Modal>
    );
}